import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FileText,
  Loader2,
  Megaphone,
  Save,
  TriangleAlert,
} from "lucide-react";
import {
  type CampaignAudienceUploadDto,
  type CampaignDto,
  type CampaignMessageCoverageEntry,
  type CampaignRecipientListItem,
  type CampaignVariableMappingEntry,
  type WhatsAppConnectionResponse,
  type WhatsAppTemplateDto,
} from "@multi-chat/shared";
import {
  getCampaignMessageCoverage,
  updateCampaignMessage,
} from "../../api/campaigns-api.js";
import { getConnection, listWhatsAppTemplates } from "../../api/whatsapp-api.js";
import { ChannelIcon } from "../../components/channel/ChannelIcon.js";
import { Button } from "../../components/ui/button/index.js";
import {
  buildClientTemplatePresentation,
  discoverTemplateSendSlots,
  slotStorageKey,
  type TemplateSendSlot,
} from "../inbox/template-send.js";
import {
  templateHasStoredHeaderImage,
  useWhatsAppTemplateHeaderImageUrl,
} from "../whatsapp/use-whatsapp-template-header-image.js";
import {
  buildPreviewValuesMap,
  CAMPAIGN_MESSAGE_MISSING_PLACEHOLDER,
  campaignApprovedTemplates,
  canOpenCampaignMessageStep,
  compatibleMappingAfterTemplateChange,
  draftFromCampaign,
  excerptContainingVariable,
  isMessageDraftDirty,
  isMessageDraftReadyToSave,
  mappingEntryLabel,
  messageConfigurationBlocker,
  messageConfigurationBlockerKey,
  parameterKeyUsages,
  resolveMappingValue,
  senderDisplayName,
  splitTemplatePreviewSegments,
  uniqueRequiredParameterKeys,
  unrenderedVariableKeys,
  uploadPersonalizationColumns,
  usableWhatsAppSenders,
  variableTokenLabel,
  type CampaignMessageDraft,
} from "./campaign-message-presentation.js";
import { CampaignLivePreview, CampaignPreviewRecipientPicker } from "./CampaignLivePreview.js";
import { campaignAudiencePrimaryActionClass } from "./CampaignManageRecipientsButton.js";
import { CampaignPhonePreview } from "./CampaignPhonePreview.js";
import { CampaignPreviewSegments, CampaignVariableToken } from "./CampaignPreviewSegments.js";
import { mapCampaignError } from "./campaigns-errors.js";
import { useNotifications } from "../notifications/notification-context.js";
import { CampaignTemplateSelect } from "./CampaignTemplateSelect.js";
import { CampaignTemplateVariablesSection } from "./CampaignTemplateVariablesSection.js";
import { CampaignVariableSourceDialog } from "./CampaignVariableSourceDialog.js";
import { CampaignWhatsappSenderCard } from "./CampaignWhatsappSenderCard.js";
import { OrganizationMessageBalanceBadge } from "../organizations/OrganizationMessageBalanceBadge.js";
import {
  campaignContentAsideClass,
  campaignContentFormBodyClass,
  campaignContentFormClass,
  campaignContentFormScrollClass,
  campaignContentLayoutClass,
  campaignContentPreviewAreaClass,
  campaignContentSaveClass,
  campaignContentToolbarClass,
  campaignContentToolbarRowClass,
} from "./campaigns-presentation.js";

export function CampaignMessageStep({
  organizationId,
  role,
  campaign,
  recipients,
  upload,
  canMutate,
  onCampaignUpdated,
  onDraftDirtyChange,
  saveActionSlot,
}: {
  organizationId: number;
  role?: string;
  campaign: CampaignDto;
  recipients: readonly CampaignRecipientListItem[];
  upload: CampaignAudienceUploadDto | null;
  canMutate: boolean;
  onCampaignUpdated: (campaign: CampaignDto) => void;
  /** Reports only the existing local dirty flag; the draft itself stays here. */
  onDraftDirtyChange?: (dirty: boolean) => void;
  /** Outer Content header slot that hosts the Save Message action. */
  saveActionSlot?: HTMLElement | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pushNotification } = useNotifications();
  const [draft, setDraft] = useState<CampaignMessageDraft>(() => draftFromCampaign(campaign));
  const [connection, setConnection] = useState<WhatsAppConnectionResponse | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplateDto[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState<unknown>(null);
  const [coverage, setCoverage] = useState<CampaignMessageCoverageEntry[]>([]);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [mappingKey, setMappingKey] = useState<string | null>(null);
  const returnTokenIdRef = useRef<string | null>(null);
  const [previewRecipientId, setPreviewRecipientId] = useState<string | null>(null);

  const audienceConfirmed = canOpenCampaignMessageStep({
    snapshotStale: campaign.snapshotStale,
    readyRecipientCount: campaign.audienceSnapshotSummary?.readyRecipients ?? 0,
  });

  useEffect(() => {
    setDraft(draftFromCampaign(campaign));
  }, [
    campaign.id,
    campaign.updatedAt,
    campaign.platformEndpointId,
    campaign.whatsappTemplateId,
    campaign.variableMapping,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    setLoadingMeta(true);
    setMetaError(null);

    void (async () => {
      try {
        const [conn, listed] = await Promise.all([
          getConnection(organizationId, controller.signal),
          listWhatsAppTemplates(organizationId, controller.signal),
        ]);
        if (controller.signal.aborted) return;
        setConnection(conn);
        setTemplates(campaignApprovedTemplates(listed.items));
      } catch (caught) {
        if (controller.signal.aborted) return;
        setMetaError(caught);
      } finally {
        if (!controller.signal.aborted) setLoadingMeta(false);
      }
    })();

    return () => controller.abort();
  }, [organizationId]);

  useEffect(() => {
    if (!audienceConfirmed) {
      setCoverage([]);
      return;
    }
    const controller = new AbortController();
    setCoverageLoading(true);
    void (async () => {
      try {
        const response = await getCampaignMessageCoverage(
          organizationId,
          campaign.id,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setCoverage(response.coverage);
      } catch {
        if (controller.signal.aborted) return;
        setCoverage([]);
      } finally {
        if (!controller.signal.aborted) setCoverageLoading(false);
      }
    })();
    return () => controller.abort();
  }, [
    audienceConfirmed,
    organizationId,
    campaign.id,
    campaign.updatedAt,
    campaign.messageConfigured,
  ]);

  const senders = useMemo(() => usableWhatsAppSenders(connection), [connection]);

  useEffect(() => {
    if (draft.platformEndpointId != null) return;
    if (senders.length !== 1) return;
    setDraft((current) => ({
      ...current,
      platformEndpointId: senders[0]!.platformEndpointId,
    }));
  }, [draft.platformEndpointId, senders]);

  useEffect(() => {
    if (recipients.length === 0) {
      setPreviewRecipientId(null);
      return;
    }
    setPreviewRecipientId((current) => {
      if (current != null && recipients.some((item) => item.id === current)) {
        return current;
      }
      return recipients[0]!.id;
    });
  }, [recipients]);

  const selectedTemplate =
    draft.whatsappTemplateId != null
      ? (templates.find((item) => item.id === draft.whatsappTemplateId) ?? null)
      : null;

  const headerImageUrl = useWhatsAppTemplateHeaderImageUrl(
    organizationId,
    selectedTemplate?.id ?? null,
    templateHasStoredHeaderImage(selectedTemplate),
  );

  const templateComponents =
    selectedTemplate?.components ?? campaign.templateSnapshot.components ?? null;

  const discovery = useMemo(
    () => (templateComponents != null ? discoverTemplateSendSlots(templateComponents) : null),
    [templateComponents],
  );

  const requiredKeys = useMemo(() => {
    if (!discovery || discovery.kind !== "supported") return [];
    return uniqueRequiredParameterKeys(discovery.slots);
  }, [discovery]);

  const uploadColumns = useMemo(
    () => uploadPersonalizationColumns({ upload, recipients }),
    [upload, recipients],
  );

  const previewPresentation = useMemo(() => {
    if (!discovery || discovery.kind !== "supported") return null;
    const templateName =
      selectedTemplate?.name ??
      campaign.templateSnapshot.name ??
      t("campaigns.message.template");
    const { valuesBySlotKey, missingParameterKeys } = buildPreviewValuesMap(
      draft.variableMapping,
      recipients.find((item) => item.id === previewRecipientId)?.personalizationSnapshot ?? null,
      discovery.slots,
    );
    return {
      presentation: buildClientTemplatePresentation({
        templateName,
        headerText: discovery.headerText,
        headerFormat: discovery.headerFormat,
        headerImageUrl,
        headerImageMissing: selectedTemplate?.headerImageMissing === true,
        bodyText: discovery.bodyText,
        footerText: discovery.footerText,
        buttons: discovery.buttons,
        slots: discovery.slots,
        valuesBySlotKey,
      }),
      missingParameterKeys,
      language:
        selectedTemplate?.language ?? campaign.templateSnapshot.language ?? null,
    };
  }, [
    campaign.templateSnapshot.language,
    campaign.templateSnapshot.name,
    discovery,
    draft.variableMapping,
    previewRecipientId,
    recipients,
    selectedTemplate,
    headerImageUrl,
    t,
  ]);

  const dirty = isMessageDraftDirty(draft, campaign);

  useEffect(() => {
    onDraftDirtyChange?.(dirty);
  }, [dirty, onDraftDirtyChange]);

  useEffect(() => () => onDraftDirtyChange?.(false), [onDraftDirtyChange]);

  const readyToSave =
    canMutate &&
    isMessageDraftReadyToSave({ draft, requiredParameterKeys: requiredKeys }) &&
    discovery?.kind === "supported";

  const whatsAppConnected = connection?.state === "CONNECTED" && senders.length > 0;
  const mappingEntry =
    mappingKey != null ? (draft.variableMapping.variables[mappingKey] ?? null) : null;

  const coverageByKey = useMemo(() => {
    const map = new Map<string, CampaignMessageCoverageEntry>();
    for (const entry of coverage) {
      map.set(entry.parameterKey, entry);
    }
    return map;
  }, [coverage]);

  const mappedKeyCount = requiredKeys.filter(
    (key) => draft.variableMapping.variables[key] != null,
  ).length;

  const configBlocker = messageConfigurationBlocker({
    hasSender: draft.platformEndpointId != null,
    hasTemplate: draft.whatsappTemplateId != null,
    discoveryKind: discovery?.kind ?? null,
    requiredParameterKeys: requiredKeys,
    mappedKeyCount,
    dirty,
  });

  const missingLabels = useMemo(() => {
    if (!previewPresentation) return [];
    return previewPresentation.missingParameterKeys.map((key) => {
      const entry = draft.variableMapping.variables[key];
      return entry ? mappingEntryLabel(t, entry) : variableTokenLabel(key);
    });
  }, [draft.variableMapping.variables, previewPresentation, t]);

  const previewPersonalization =
    recipients.find((item) => item.id === previewRecipientId)?.personalizationSnapshot ?? null;

  const previewSlots = useMemo(() => {
    if (!discovery || discovery.kind !== "supported" || !previewPresentation) return null;
    const values = buildPreviewValuesMap(
      draft.variableMapping,
      previewPersonalization,
      discovery.slots,
    ).valuesBySlotKey;
    const header =
      discovery.headerFormat === "TEXT" && discovery.headerText
        ? splitTemplatePreviewSegments({
            text: discovery.headerText,
            componentType: "HEADER",
            slots: discovery.slots,
            valuesBySlotKey: values,
          })
        : null;
    const body = discovery.bodyText
      ? splitTemplatePreviewSegments({
          text: discovery.bodyText,
          componentType: "BODY",
          slots: discovery.slots,
          valuesBySlotKey: values,
        })
      : null;
    const buttonSlots = new Map<number, TemplateSendSlot>();
    for (const slot of discovery.slots) {
      if (slot.componentType === "BUTTON" && slot.buttonIndex != null) {
        buttonSlots.set(slot.buttonIndex, slot);
      }
    }
    const unrenderedKeys = unrenderedVariableKeys({
      slots: discovery.slots,
      headerFormat: discovery.headerFormat,
      headerText: discovery.headerText,
      bodyText: discovery.bodyText,
    });
    return { header, body, buttonSlots, values, unrenderedKeys };
  }, [discovery, draft.variableMapping, previewPresentation, previewPersonalization]);

  const openMapping = (parameterKey: string, tokenId: string) => {
    returnTokenIdRef.current = tokenId;
    setMappingKey(parameterKey);
  };

  const mappingExcerpt = (() => {
    if (mappingKey == null || discovery?.kind !== "supported") return null;
    const token = variableTokenLabel(mappingKey);
    const sources = [
      discovery.bodyText,
      discovery.headerText,
      ...discovery.slots
        .filter((slot) => slot.parameterKey === mappingKey && slot.urlTemplate)
        .map((slot) => slot.urlTemplate ?? null),
    ];
    const text = sources.find((candidate) => candidate?.includes(token)) ?? null;
    return excerptContainingVariable(text, mappingKey);
  })();

  const selectTemplate = (template: WhatsAppTemplateDto) => {
    const nextDiscovery = discoverTemplateSendSlots(template.components);
    const nextKeys =
      nextDiscovery.kind === "supported"
        ? uniqueRequiredParameterKeys(nextDiscovery.slots)
        : [];
    setDraft((current) => ({
      ...current,
      whatsappTemplateId: template.id,
      variableMapping: compatibleMappingAfterTemplateChange(
        current.variableMapping,
        nextKeys,
      ),
    }));
  };

  const setMappingEntry = (parameterKey: string, entry: CampaignVariableMappingEntry) => {
    setDraft((current) => ({
      ...current,
      variableMapping: {
        version: 1,
        variables: {
          ...current.variableMapping.variables,
          [parameterKey]: entry,
        },
      },
    }));
  };

  const saveMessage = async () => {
    if (!readyToSave || draft.platformEndpointId == null || draft.whatsappTemplateId == null) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const response = await updateCampaignMessage(organizationId, campaign.id, {
        platformEndpointId: draft.platformEndpointId,
        whatsappTemplateId: draft.whatsappTemplateId,
        variableMapping: draft.variableMapping,
      });
      onCampaignUpdated(response.campaign);
      if (response.coverage) {
        setCoverage(response.coverage);
      } else if (audienceConfirmed) {
        const coverageResponse = await getCampaignMessageCoverage(
          organizationId,
          campaign.id,
        );
        setCoverage(coverageResponse.coverage);
      }
      pushNotification({ message: t("campaigns.toasts.messageSaved") });
    } catch (caught) {
      setSaveError(caught);
      pushNotification({
        kind: "error",
        message: mapCampaignError(t, caught, t("campaigns.errors.requestFailed")),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loadingMeta) {
    return (
      <div className="grid place-items-center py-16" data-testid="campaign-message-loading">
        <p className="flex items-center gap-2 text-muted" aria-live="polite">
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          {t("campaigns.loading")}
        </p>
      </div>
    );
  }

  if (metaError) {
    return (
      <p
        className="m-0 rounded-md border border-error/40 bg-error-muted px-3 py-2 text-[0.8125rem] text-error"
        role="alert"
      >
        {mapCampaignError(t, metaError, t("campaigns.errors.requestFailed"))}
      </p>
    );
  }

  if (!whatsAppConnected) {
    return (
      <section
        className="grid w-full max-w-xl justify-self-start gap-2 rounded-lg border border-line/70 bg-surface px-3.5 py-3"
        data-testid="campaign-message-no-whatsapp"
      >
        <span
          className="inline-grid size-8 place-items-center rounded-lg border border-line/70 bg-whatsapp-muted text-whatsapp"
          aria-hidden="true"
        >
          <ChannelIcon platformType="WHATSAPP" size={16} />
        </span>
        <div className="grid min-w-0 gap-1">
          <h2 className="m-0 text-[0.875rem] font-semibold tracking-[-0.01em]">
            {t("campaigns.message.noWhatsApp")}
          </h2>
          <p className="m-0 text-[0.8125rem] leading-snug text-muted">
            {t("campaigns.message.noWhatsAppDescription")}
          </p>
        </div>
        <div>
          <Button asChild size="sm">
            <Link to="/whatsapp">{t("campaigns.message.connectWhatsApp")}</Link>
          </Button>
        </div>
      </section>
    );
  }

  if (templates.length === 0 && draft.whatsappTemplateId == null) {
    return (
      <section
        className="grid w-full max-w-xl justify-self-start gap-2 rounded-lg border border-line/70 bg-surface px-3.5 py-3"
        data-testid="campaign-message-no-templates"
      >
        <span
          className="inline-grid size-8 place-items-center rounded-lg border border-line/70 bg-surface-muted text-muted"
          aria-hidden="true"
        >
          <FileText size={16} />
        </span>
        <div className="grid min-w-0 gap-1">
          <h2 className="m-0 text-[0.875rem] font-semibold tracking-[-0.01em]">
            {t("campaigns.message.noTemplates")}
          </h2>
          <p className="m-0 text-[0.8125rem] leading-snug text-muted">
            {t("campaigns.message.noTemplatesDescription")}
          </p>
        </div>
        <div>
          <Button asChild size="sm" variant="outline">
            <Link to="/whatsapp">{t("campaigns.message.manageTemplates")}</Link>
          </Button>
        </div>
      </section>
    );
  }

  const continueHint =
    configBlocker != null ? t(messageConfigurationBlockerKey(configBlocker)) : null;
  const selectedSender =
    senders.find((phone) => phone.platformEndpointId === draft.platformEndpointId) ??
    (senders.length === 1 ? senders[0]! : null);

  const saveAction = canMutate ? (
    <Button
      type="button"
      size="sm"
      className={campaignAudiencePrimaryActionClass}
      disabled={!readyToSave || saving || !dirty}
      onClick={() => void saveMessage()}
      data-testid="campaign-message-save"
    >
      {saving ? (
        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
      ) : (
        <Save size={14} aria-hidden="true" />
      )}
      {saving ? t("campaigns.saving") : t("campaigns.message.save")}
    </Button>
  ) : null;

  return (
    <div className="grid min-w-0 gap-3" data-testid="campaign-message-step">
      {saveAction && saveActionSlot ? createPortal(saveAction, saveActionSlot) : null}

      <div className={campaignContentLayoutClass} data-testid="campaign-content-layout">
        <div className={campaignContentFormClass} data-testid="campaign-content-form">
          <header
            className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/60 px-4 py-3"
            data-testid="campaign-content-form-header"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="inline-grid size-9 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface-muted text-ink"
                aria-hidden="true"
              >
                <Megaphone size={16} />
              </span>
              <div className="grid min-w-0 gap-0.5">
                <h3 className="m-0 text-[0.875rem] font-semibold tracking-[-0.01em]">
                  {t("campaigns.content.formTitle")}
                </h3>
                <p className="m-0 text-[0.75rem] leading-snug text-muted">
                  {t("campaigns.content.formDescription")}
                </p>
              </div>
            </div>
            <OrganizationMessageBalanceBadge organizationId={organizationId} role={role} />
          </header>

          <div className={campaignContentFormScrollClass} data-testid="campaign-content-form-scroll">
            <div className={campaignContentFormBodyClass} data-testid="campaign-content-form-body">
              {!canMutate ? (
                <p className="m-0 text-[0.8125rem] text-muted">{t("campaigns.message.readOnly")}</p>
              ) : null}

              {saveError ? (
                <p
                  className="m-0 rounded-lg border border-error/40 bg-error-muted px-3 py-2 text-[0.8125rem] text-error"
                  role="alert"
                >
                  {mapCampaignError(t, saveError, t("campaigns.errors.requestFailed"))}
                </p>
              ) : null}

              {campaign.snapshotStale ? (
                <div
                  className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-muted px-3 py-2"
                  data-testid="campaign-message-audience-stale"
                >
                  <TriangleAlert size={14} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
                  <div>
                    <p className="m-0 text-[0.8125rem] font-semibold">
                      {t("campaigns.message.audienceStaleTitle")}
                    </p>
                    <p className="m-0 text-[0.75rem] text-warning">
                      {t("campaigns.message.audienceStaleMessage")}
                    </p>
                  </div>
                </div>
              ) : null}

              <CampaignWhatsappSenderCard
                senders={senders}
                selectedEndpointId={draft.platformEndpointId}
                canMutate={canMutate}
                onSelectEndpoint={(platformEndpointId) =>
                  setDraft((current) => ({ ...current, platformEndpointId }))
                }
              />

              {discovery?.kind === "supported" ? (
                <CampaignTemplateVariablesSection
                  requiredKeys={requiredKeys}
                  unrenderedKeys={previewSlots?.unrenderedKeys ?? []}
                  mappedCount={mappedKeyCount}
                  mapping={draft.variableMapping}
                  canMutate={canMutate}
                  onMap={openMapping}
                />
              ) : null}
            </div>
          </div>
        </div>

        <aside className={campaignContentAsideClass} data-testid="campaign-content-workspace">
          <div className={campaignContentToolbarClass} data-testid="campaign-content-preview-toolbar">
            <div className={campaignContentToolbarRowClass}>
              <CampaignTemplateSelect
                templates={templates}
                selectedTemplateId={draft.whatsappTemplateId}
                disabled={!canMutate}
                onSelect={selectTemplate}
              />
              <CampaignPreviewRecipientPicker
                recipients={recipients}
                previewRecipientId={previewRecipientId}
                onPreviewRecipientChange={setPreviewRecipientId}
                className="justify-center @[30rem]:justify-end"
              />
            </div>
            {discovery?.kind === "unsupported" ? (
              <p className="m-0 mt-1.5 text-[0.75rem] text-warning" role="status">
                {t("campaigns.message.blockers.unsupported")}
              </p>
            ) : null}
          </div>
          <div className={campaignContentPreviewAreaClass}>
            <CampaignLivePreview
              recipients={recipients}
              previewRecipientId={previewRecipientId}
              onPreviewRecipientChange={setPreviewRecipientId}
              presentation={previewPresentation?.presentation ?? null}
              language={previewPresentation?.language ?? null}
              missingLabels={missingLabels}
              workspace
              renderFrame={(preview) => (
                <CampaignPhonePreview
                  senderName={selectedSender ? senderDisplayName(selectedSender) : null}
                  senderPhone={selectedSender?.phoneNumber ?? null}
                  contentKey={String(draft.whatsappTemplateId ?? "none")}
                >
                  {preview}
                </CampaignPhonePreview>
              )}
              textSlots={
                previewSlots
                  ? {
                      ...(previewSlots.header
                        ? {
                            header: (
                              <CampaignPreviewSegments
                                segments={previewSlots.header}
                                entries={draft.variableMapping.variables}
                                canEdit={canMutate}
                                onActivate={openMapping}
                              />
                            ),
                          }
                        : {}),
                      ...(previewSlots.body
                        ? {
                            body: (
                              <CampaignPreviewSegments
                                segments={previewSlots.body}
                                entries={draft.variableMapping.variables}
                                canEdit={canMutate}
                                onActivate={openMapping}
                              />
                            ),
                          }
                        : {}),
                    }
                  : undefined
              }
              renderButtonAccessory={
                previewSlots && previewSlots.buttonSlots.size > 0
                  ? (buttonIndex) => {
                      const slot = previewSlots.buttonSlots.get(buttonIndex);
                      if (!slot) return null;
                      const value = previewSlots.values.get(slotStorageKey(slot)) ?? null;
                      return (
                        <CampaignVariableToken
                          variant="button"
                          identity={{
                            componentType: "BUTTON",
                            parameterKey: slot.parameterKey,
                            buttonIndex,
                          }}
                          entry={draft.variableMapping.variables[slot.parameterKey] ?? null}
                          sampleValue={
                            value != null && value !== CAMPAIGN_MESSAGE_MISSING_PLACEHOLDER
                              ? value
                              : null
                          }
                          canEdit={canMutate}
                          onActivate={openMapping}
                        />
                      );
                    }
                  : undefined
              }
            />
          </div>
        </aside>
      </div>

      <div className={campaignContentSaveClass} data-testid="campaign-content-save">
        {continueHint ? (
          <p
            className="m-0 text-[0.75rem] leading-snug text-muted"
            data-testid="campaign-message-continue-hint"
          >
            {continueHint}
          </p>
        ) : null}
      </div>

      {mappingKey != null ? (
        <CampaignVariableSourceDialog
          open
          parameterKey={mappingKey}
          initial={mappingEntry}
          uploadColumns={uploadColumns}
          usages={
            discovery?.kind === "supported" ? parameterKeyUsages(discovery.slots, mappingKey) : []
          }
          excerpt={mappingExcerpt}
          coverage={coverageByKey.get(mappingKey) ?? null}
          sampleValueFor={(entry) =>
            previewPersonalization ? resolveMappingValue(entry, previewPersonalization) : null
          }
          canEdit={canMutate}
          onOpenChange={(open) => {
            if (!open) setMappingKey(null);
          }}
          onSave={(entry) => setMappingEntry(mappingKey, entry)}
          onCloseAutoFocus={(event) => {
            const tokenId = returnTokenIdRef.current;
            if (tokenId == null) return;
            event.preventDefault();
            document
              .querySelector<HTMLElement>(`[data-token-id="${CSS.escape(tokenId)}"]`)
              ?.focus();
          }}
        />
      ) : null}
    </div>
  );
}
